import mapboxgl from 'mapbox-gl'

/**
 * Safe imperative Mapbox operations that tolerate a missing/reloading style.
 *
 * When the map is removed or its style is swapped (e.g. after a WebGL context
 * loss or a network blip mid-load), Mapbox clears its internal `style`. At that
 * point `addLayer`/`addSource`/`removeLayer`/`removeSource` — and even the
 * `getLayer`/`getSource` guards people reach for — throw
 * `Cannot read properties of undefined`, because they dereference the missing
 * style. That error escapes render/effects and trips a React error boundary,
 * showing the "Falha nas camadas do mapa" modal.
 *
 * `getStyle()` is the one accessor that stays safe here: Mapbox guards it with
 * `if (this.style)` and returns `undefined` instead of throwing once the style
 * is gone. We use it to gate every operation below, so each becomes a no-op
 * when there is nothing live to operate on.
 */

type MaybeMap = mapboxgl.Map | null | undefined

// True only when the map still has a live style to operate on.
export function hasLiveStyle(map: MaybeMap): map is mapboxgl.Map {
  return !!map && !!map.getStyle()
}

export function safeRemoveLayer(map: MaybeMap, id: string): void {
  if (!hasLiveStyle(map)) return
  if (map.getLayer(id)) map.removeLayer(id)
}

export function safeRemoveSource(map: MaybeMap, id: string): void {
  if (!hasLiveStyle(map)) return
  if (map.getSource(id)) map.removeSource(id)
}

export function safeAddSource(map: MaybeMap, id: string, source: mapboxgl.AnySourceData): void {
  if (!hasLiveStyle(map)) return
  // A leftover id from before a style reload would make `addSource` throw
  // "There is already a source with ID ...".
  if (map.getSource(id)) return
  map.addSource(id, source)
}

export function safeAddLayer(map: MaybeMap, layer: mapboxgl.AnyLayer, beforeId?: string): void {
  if (!hasLiveStyle(map)) return
  // A leftover id would make `addLayer` throw "Layer ... already exists".
  if (map.getLayer(layer.id)) return
  // Only anchor before an existing layer — a stale `beforeId` (e.g. a stack
  // anchor cleared by a style reload) makes `addLayer` throw
  // "Layer ... does not exist on this map.".
  const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined
  map.addLayer(layer, before)
}

export function safeSetPaintProperty(map: MaybeMap, layerId: string, name: string, value: unknown): void {
  if (!hasLiveStyle(map)) return
  if (!map.getLayer(layerId)) return
  // Generic helper — the paint-property name/value are validated by the caller.
  map.setPaintProperty(layerId, name as never, value as never)
}
